import { useState } from 'react';
import { motion } from 'framer-motion';
import { Wallet, ChevronDown, LogOut, Copy, Check, Zap } from 'lucide-react';
import useStore from '../store/useStore';

export default function Header() {
  const { wallet, connectWallet, disconnectWallet } = useStore();
  const [showDropdown, setShowDropdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      await connectWallet();
    } catch (err: any) {
      setError(err.message || '连接失败');
      console.error('连接钱包失败:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleCopyAddress = () => {
    if (wallet.address) {
      navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="relative">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyber-purple to-cyber-blue flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-cyber-purple to-cyber-blue blur-lg opacity-50" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold bg-gradient-to-r from-cyber-purple via-cyber-blue to-cyber-green bg-clip-text text-transparent">
                MONAD x402
              </h1>
              <p className="text-xs text-gray-500">Crypto Prediction</p>
            </div>
          </motion.div>

          {/* 钱包按钮 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            {!wallet.isConnected ? (
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="btn-cyber flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyber-purple to-cyber-blue text-white font-medium transition-all hover:shadow-lg hover:shadow-cyber-purple/30 disabled:opacity-50"
              >
                <Wallet className="w-4 h-4" />
                {isConnecting ? '连接中...' : '连接钱包'}
              </button>
            ) : (
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg bg-cyber-dark/80 border border-cyber-purple/30 hover:border-cyber-purple/60 transition-all"
                >
                  {/* 状态指示灯 */}
                  <div className="relative">
                    <div className="w-2 h-2 rounded-full bg-cyber-green" />
                    <div className="absolute inset-0 w-2 h-2 rounded-full bg-cyber-green animate-ping" />
                  </div>
                  
                  {/* 地址和余额 */}
                  <div className="text-left">
                    <p className="text-sm font-mono text-white">
                      {formatAddress(wallet.address!)}
                    </p>
                    <p className="text-xs text-cyber-blue">
                      {parseFloat(wallet.balance).toFixed(4)} MON
                    </p>
                  </div>
                  
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* 下拉菜单 */}
                {showDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute right-0 mt-2 w-64 rounded-lg glass border border-cyber-purple/30 overflow-hidden"
                  >
                    <div className="p-3 border-b border-cyber-purple/20">
                      <p className="text-xs text-gray-400 mb-1">钱包地址</p>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-white font-mono flex-1 truncate">
                          {wallet.address}
                        </code>
                        <button
                          onClick={handleCopyAddress}
                          className="p-1 hover:bg-cyber-purple/20 rounded transition-colors"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-cyber-green" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="p-3 border-b border-cyber-purple/20">
                      <p className="text-xs text-gray-400 mb-1">网络</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-cyber-green" />
                        <span className="text-sm text-white">Monad Testnet</span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        disconnectWallet();
                        setShowDropdown(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-cyber-red hover:bg-cyber-red/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      断开连接
                    </button>
                  </motion.div>
                )}
              </div>
            )}
            
            {/* 错误提示 */}
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute top-full mt-2 right-0 text-xs text-cyber-red whitespace-nowrap"
              >
                {error}
              </motion.p>
            )}
          </motion.div>
        </div>
      </div>
    </header>
  );
}

